import { catchError, throwError } from 'rxjs';
import { inject } from '@angular/core';
import { HttpInterceptorFn } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { GlobalErrorService } from 'src/app/services/global-error.service';
import { HealthService } from 'src/app/services/health.service';
import { LogService } from 'src/app/services/log.service';
import { logger } from 'src/app/utils';

/*
 * Endpoints whose errors are handled by their own inline UI and must NOT trigger
 * the global error dialog (see online-payment / in-store-payment components).
 * */
const SKIPPED_ENDPOINTS = [
  'placeOrder',
  'retryCheckout',
  'confirmPaymentStatus',
  'confirmOrder',
  'downloadPdf',
];

function shouldSkip(url: string): boolean {
  if (url === environment.healthUrl) return true; // avoid loop on the health ping
  if (url.includes('/i18n/') || url.endsWith('.json')) return true; // translation/asset loads
  return SKIPPED_ENDPOINTS.some((e) => url.includes(e));
}

/*
 * Catches any failed backend request and shows a single global error dialog,
 * regardless of the form step the user is on. The error is always re-thrown so
 * existing per-request error callbacks keep working.
 * */
export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const globalErrorService = inject(GlobalErrorService);
  const healthService = inject(HealthService);
  const logService = inject(LogService);

  return next(req).pipe(
    catchError((error) => {
      if (!shouldSkip(req.url)) {
        const context = { source: 'http-interceptor', requestUrl: req.url, error };
        // Report every failed request for debugging (status 0 / CORS / iframe blocking).
        logService.logError(context);
        // open() is a no-op when the dialog is already visible, so the health
        // check only fires once even if several requests fail at the same time.
        // The report is what the user can copy from the dialog into the report email.
        // isServerError=false: this is a client-side / network error, not a backend failure.
        if (globalErrorService.open(logService.buildReport(context), false)) {
          healthService
            .check()
            .subscribe((ok) =>
              logger.i('Backend health on API error', {
                url: req.url,
                status: error?.status,
                healthy: ok,
              }),
            );
        }
      }
      return throwError(() => error);
    }),
  );
};
